// ─── Book a session with your coach (member side) ───
// Home card ("Sessions"), a booking sheet (session type → date strip + time grid in the
// member's zone → confirm with a note) and "My sessions" (cancel with a reason, add to
// calendar as a .ics file). Refreshed on the `booking` org ping and on foreground.
// API: booking_options, available_slots, book_session, my_bookings, cancel_booking,
// booking_ics. Rules and formatting: utils/booking.js.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { V, Haptic } from '../utils/theme';
import { Icons } from './Icons';
import { Card, Btn, SuccessToastCtrl } from './ui';
import { FullSheet } from './FullSheet';
import { onAccent } from './kit';
import { orgApi, orgErr, personName } from '../utils/org';
import { useOrgFeed } from '../utils/orgFeed';
import { downloadText } from '../tabs/admin/orgs/helpers';
import {
  BOOKING_OPTIONS_CACHE_KEY, MY_BOOKINGS_CACHE_KEY, BOOKING_NOTE_MAX, CANCEL_REASON_MAX,
  memberTimeZone, zonesDiffer, zoneName, slotsByDay, dateStrip, slotRequest, dateKeyIn, fmtTime, fmtDate,
  durationLabel, kindLabel, whenLabel, splitBookings, canCancel, statusLabel, icsFilename, bookingErrorText,
} from '../utils/booking';

const eyebrow = () => ({ fontSize: 10, fontWeight: 700, color: V.accent, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 });
const inputStyle = () => ({ width: '100%', boxSizing: 'border-box', paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, borderRadius: 12,
  background: V.inputBg, border: `1px solid ${V.cardBorder}`, color: V.text, fontSize: 16, fontFamily: V.font, outline: 'none', resize: 'vertical', lineHeight: 1.45 });

// Fetch booking_ics and save it as a calendar file.
async function addToCalendar(b) {
  try {
    const r = await orgApi('booking_ics', { booking_id: b.id });
    if (!r || typeof r.ics !== 'string') throw new Error('no ics');
    downloadText(icsFilename(r.filename, b.starts_at), r.ics, 'text/calendar;charset=utf-8');
  } catch (e) {
    SuccessToastCtrl.error(bookingErrorText(orgErr(e).code));
  }
}

export function useBookings() {
  const options = useOrgFeed('booking_options', { kind: 'booking', cacheKey: BOOKING_OPTIONS_CACHE_KEY });
  const mine = useOrgFeed('my_bookings', { kind: 'booking', cacheKey: MY_BOOKINGS_CACHE_KEY });
  const types = Array.isArray(options.data && options.data.session_types) ? options.data.session_types : [];
  const bookings = Array.isArray(mine.data && mine.data.bookings) ? mine.data.bookings : [];
  return { enabled: options.enabled, types, bookings, reloadTypes: options.load, reloadBookings: mine.load };
}

// ─── Home card ───
export function BookingCard() {
  const bk = useBookings();
  const [sheet, setSheet] = useState(null); // 'book' | 'mine'
  const tz = memberTimeZone();
  const { upcoming } = splitBookings(bk.bookings);
  const close = useCallback(() => setSheet(null), []);
  if (!bk.enabled) return null;
  const sheetEl = sheet === 'book' ? <BookingSheet types={bk.types} onClose={close} onBooked={bk.reloadBookings} onSlotsStale={bk.reloadTypes} />
    : sheet === 'mine' ? <MySessionsSheet bookings={bk.bookings} onClose={close} onChanged={bk.reloadBookings} onBook={bk.types.length ? () => setSheet('book') : null} /> : null;
  if (!bk.types.length && !upcoming.length) return sheetEl;
  const next = upcoming[0];
  return (
    <div data-testid="booking-card">
      <Card style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={eyebrow()}>From your coach</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: V.text }}>{next ? 'Next session' : 'Sessions'}</div>
            {next ? (
              <div data-testid="booking-next" style={{ fontSize: 12, color: V.text2, marginTop: 2 }}>
                <span style={{ fontWeight: 600, color: V.text }}>{next.title}</span> · {whenLabel(next, tz)}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: V.text3, marginTop: 2 }}>Book a session with your coach</div>
            )}
          </div>
          {Icons.calendar({ size: 18, color: V.accent })}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {bk.types.length > 0 && <Btn onClick={() => setSheet('book')} s={{ flex: 1, minHeight: 40, padding: '8px 14px', fontSize: 13 }}>Book a session</Btn>}
          {bk.bookings.length > 0 && <Btn v="secondary" onClick={() => setSheet('mine')} s={{ flex: 1, minHeight: 40, padding: '8px 14px', fontSize: 13 }}>
            My sessions{upcoming.length ? ` (${upcoming.length})` : ''}</Btn>}
        </div>
      </Card>
      {sheetEl}
    </div>
  );
}

// ─── Booking sheet ───
function useSheetKeys(onClose, focusRef, dep) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { focusRef.current?.focus(); }, [focusRef, dep]);
}

export function BookingSheet({ types, onClose, onBooked, onSlotsStale }) {
  const tz = memberTimeZone();
  const [type, setType] = useState(types.length === 1 ? types[0] : null);
  const [slots, setSlots] = useState(null);        // { coach_timezone, duration_min, slots } | null while loading
  const [day, setDay] = useState(null);
  const [pick, setPick] = useState(null);           // chosen ISO start
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);           // booked booking
  const headRef = useRef(null);
  const step = done ? 'done' : !type ? 'type' : pick ? 'confirm' : 'slots';
  useSheetKeys(onClose, headRef, step);

  const loadSlots = useCallback(async (t, keepDay) => {
    setSlots(null); setErr(null);
    try {
      const r = await orgApi('available_slots', slotRequest(t));
      setSlots({ coach_timezone: r?.coach_timezone || t.coach_timezone, duration_min: r?.duration_min || t.duration_min, slots: Array.isArray(r?.slots) ? r.slots : [] });
      if (!keepDay) setDay(null);
    } catch (e) {
      const er = orgErr(e);
      setSlots({ coach_timezone: t.coach_timezone, duration_min: t.duration_min, slots: [] });
      setErr(bookingErrorText(er.code));
      if (er.status === 404 && onSlotsStale) onSlotsStale();
    }
  }, [onSlotsStale]);
  useEffect(() => { if (type) loadSlots(type); }, [type, loadSlots]);

  const grouped = useMemo(() => slotsByDay(slots?.slots, tz), [slots, tz]);
  const req = type ? slotRequest(type) : null;
  const reqDays = req ? req.days : 0;
  // From the member's today (or the first slot, if the coach's day starts earlier) through the
  // horizon or the last slot, whichever is later.
  const strip = useMemo(() => {
    if (!reqDays) return [];
    const today = dateKeyIn(Date.now(), tz);
    const first = grouped.length && grouped[0].date < today ? grouped[0].date : today;
    const last = grouped.length ? grouped[grouped.length - 1].date : first;
    const span = Math.round((Date.parse(last + 'T00:00:00Z') - Date.parse(first + 'T00:00:00Z')) / 86400000) + 1;
    return dateStrip(first, Math.max(reqDays, span), grouped);
  }, [reqDays, grouped, tz]);
  const selDay = day || (grouped[0] && grouped[0].date) || null;
  const daySlots = (grouped.find(g => g.date === selDay) || { slots: [] }).slots;
  const coachTz = slots?.coach_timezone || type?.coach_timezone;
  const showZone = coachTz && zonesDiffer(tz, coachTz, pick ? Date.parse(pick) : Date.now());

  const book = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await orgApi('book_session', { session_type_id: type.id, starts_at: pick, ...(note.trim() ? { note: note.trim().slice(0, BOOKING_NOTE_MAX) } : {}) });
      Haptic.success();
      setDone(r?.booking || { starts_at: pick, title: type.name });
      if (onBooked) onBooked();
    } catch (e) {
      const er = orgErr(e);
      setErr(bookingErrorText(er.code));
      if (er.code === 'slot_taken') { const d = dateKeyIn(pick, tz); setPick(null); setDay(d); await loadSlots(type, true); setErr(bookingErrorText('slot_taken')); }
      else if (er.code === 'not_found' && onSlotsStale) onSlotsStale();
    }
    setBusy(false);
  };

  const back = step === 'confirm' ? () => { setPick(null); setErr(null); } : step === 'slots' && types.length > 1 ? () => { setType(null); setSlots(null); setErr(null); } : onClose;
  const footer = step === 'confirm' ? (
    <div>
      {err && <div role="alert" style={{ fontSize: 13, color: V.danger, fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>{err}</div>}
      <Btn full onClick={book} disabled={busy}>{busy ? 'Booking…' : 'Book session'}</Btn>
    </div>
  ) : null;

  return (
    <FullSheet title="Book a session" ariaLabel="Book a session" onClose={back} closeLabel={step === 'type' || step === 'done' || (step === 'slots' && types.length <= 1) ? 'Close' : 'Back'} footer={footer} zIndex={9991}>
      {step === 'type' && (
        <div>
          <h2 ref={headRef} tabIndex={-1} style={h2}>What kind of session?</h2>
          {types.length === 0 && <p style={{ fontSize: 13, color: V.text3 }}>Your coach isn't taking bookings right now.</p>}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {types.map(t => (<li key={t.id}>
              <button type="button" onClick={() => setType(t)} data-testid="session-type" style={rowBtn()}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: V.text }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: V.text3, marginTop: 2 }}>{durationLabel(t.duration_min)} · {kindLabel(t.kind)}{t.location ? ` · ${t.location}` : ''} · {personName(t.coach)}</div>
                  {t.description && <div style={{ fontSize: 12, color: V.text2, marginTop: 4, lineHeight: 1.4 }}>{t.description}</div>}
                </div>
                {Icons.chevRight({ size: 16, color: V.text3 })}
              </button>
            </li>))}
          </ul>
        </div>
      )}

      {step === 'slots' && (
        <div>
          <h2 ref={headRef} tabIndex={-1} style={h2}>{type.name}</h2>
          <div style={{ fontSize: 12, color: V.text3, marginTop: 4 }}>{durationLabel(slots?.duration_min || type.duration_min)} · {kindLabel(type.kind)} · {personName(type.coach)}</div>
          <div data-testid="booking-zone" style={{ fontSize: 12, color: V.text2, marginTop: 10, lineHeight: 1.45 }}>
            Times are in your time zone ({zoneName(tz)}).{showZone ? ` ${personName(type.coach)} is in ${coachTz.replace(/_/g, ' ')} (${zoneName(coachTz)}).` : ''}
          </div>
          {err && <div role="alert" style={{ fontSize: 13, color: V.danger, fontWeight: 600, marginTop: 10 }}>{err}</div>}
          {slots === null ? (
            <div role="status" style={{ fontSize: 13, color: V.text3, marginTop: 20 }}>Finding open times…</div>
          ) : !slots.slots.length ? (
            <div style={{ fontSize: 13, color: V.text3, marginTop: 20 }} data-testid="no-slots">No open times in the next {req.days} days. Message your coach to find a time.</div>
          ) : (<>
            <div role="tablist" aria-label="Day" data-testid="date-strip" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingTop: 14, paddingBottom: 6, WebkitOverflowScrolling: 'touch' }}>
              {strip.map(dd => {
                const on = dd.date === selDay, empty = !dd.count;
                return (
                  <button key={dd.date} type="button" role="tab" aria-selected={on} disabled={empty} onClick={() => setDay(dd.date)}
                    aria-label={`${dd.dow} ${dd.month} ${dd.day}, ${empty ? 'no times' : `${dd.count} time${dd.count === 1 ? '' : 's'}`}`}
                    style={{ flexShrink: 0, width: 54, paddingTop: 8, paddingBottom: 8, paddingLeft: 0, paddingRight: 0, borderRadius: 12, cursor: empty ? 'default' : 'pointer', fontFamily: V.font,
                      background: on ? V.accent : V.inputBg, color: on ? onAccent() : V.text, border: `1px solid ${on ? V.accent : V.cardBorder}`, opacity: empty ? 0.35 : 1,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, WebkitTapHighlightColor: 'transparent' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', opacity: .8 }}>{dd.dow}</span>
                    <span style={{ fontSize: 17, fontWeight: 800, fontFamily: V.mono }}>{dd.day}</span>
                    <span style={{ fontSize: 10, opacity: .8 }}>{dd.month}</span>
                  </button>
                );
              })}
            </div>
            <div role="group" aria-label={`Times on ${selDay ? fmtDate(selDay + 'T12:00:00Z', 'UTC') : ''}`} data-testid="time-grid"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8, marginTop: 10 }}>
              {daySlots.map(sl => (
                <button key={sl} type="button" onClick={() => { setPick(sl); setErr(null); }} style={{ minHeight: 44, borderRadius: 12, cursor: 'pointer', fontFamily: V.mono, fontSize: 14, fontWeight: 700,
                  background: V.inputBg, color: V.text, border: `1px solid ${V.cardBorder}`, WebkitTapHighlightColor: 'transparent' }}>{fmtTime(sl, tz)}</button>
              ))}
            </div>
          </>)}
        </div>
      )}

      {step === 'confirm' && (
        <div>
          <h2 ref={headRef} tabIndex={-1} style={h2}>Confirm your session</h2>
          <div data-testid="booking-summary" style={{ marginTop: 12, paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14, borderRadius: 14, background: `${V.accent}0d`, border: `1px solid ${V.accent}26` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: V.text }}>{type.name}</div>
            <div style={{ fontSize: 13, color: V.text2, marginTop: 4 }}>{whenLabel({ starts_at: pick, ends_at: new Date(Date.parse(pick) + (slots?.duration_min || type.duration_min) * 60000).toISOString() }, tz)}</div>
            {showZone && <div style={{ fontSize: 12, color: V.text3, marginTop: 2 }}>{fmtTime(pick, coachTz)} for {personName(type.coach)} ({zoneName(coachTz, Date.parse(pick))})</div>}
            <div style={{ fontSize: 12, color: V.text3, marginTop: 4 }}>{kindLabel(type.kind)}{type.location ? ` · ${type.location}` : ''} · with {personName(type.coach)}</div>
          </div>
          <label htmlFor="booking-note" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: V.text, marginTop: 18, marginBottom: 8 }}>Note for your coach <span style={{ fontWeight: 400, color: V.text3 }}>(optional)</span></label>
          <textarea id="booking-note" value={note} maxLength={BOOKING_NOTE_MAX} rows={3} placeholder="Anything you'd like to work on?" onChange={e => setNote(e.target.value)} style={inputStyle()} />
        </div>
      )}

      {step === 'done' && (
        <div role="status" data-testid="booking-done" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', paddingTop: 40, gap: 12 }}>
          <div aria-hidden="true" style={{ width: 64, height: 64, borderRadius: 32, background: `linear-gradient(135deg,${V.accent},${V.accent2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: V.glow }}>
            {Icons.check({ size: 30, color: onAccent(), strokeWidth: 3 })}
          </div>
          <div ref={headRef} tabIndex={-1} style={{ fontSize: 18, fontWeight: 700, color: V.text, outline: 'none' }}>You're booked</div>
          <div style={{ fontSize: 13, color: V.text2 }}>{done.title || type.name} · {whenLabel(done, tz)}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {done.id && <Btn v="secondary" onClick={() => addToCalendar(done)}>{Icons.calendar({ size: 14, color: V.text2 })} Add to calendar</Btn>}
            <Btn onClick={onClose}>Done</Btn>
          </div>
        </div>
      )}
    </FullSheet>
  );
}

const h2 = { fontSize: 20, fontWeight: 800, color: V.text, margin: 0, outline: 'none', lineHeight: 1.25 };
const rowBtn = () => ({ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 12,
  borderRadius: 14, background: V.inputBg, border: `1px solid ${V.cardBorder}`, cursor: 'pointer', fontFamily: V.font, WebkitTapHighlightColor: 'transparent' });

// ─── My sessions ───
export function MySessionsSheet({ bookings, onClose, onChanged, onBook }) {
  const tz = memberTimeZone();
  const { upcoming, past } = splitBookings(bookings);
  const [cancelling, setCancelling] = useState(null); // booking id
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const headRef = useRef(null);
  useSheetKeys(onClose, headRef, null);

  const cancel = async (b) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await orgApi('cancel_booking', { booking_id: b.id, ...(reason.trim() ? { reason: reason.trim().slice(0, CANCEL_REASON_MAX) } : {}) });
      setCancelling(null); setReason('');
      SuccessToastCtrl.show('Session cancelled');
      if (onChanged) onChanged();
    } catch (e) {
      const er = orgErr(e);
      setErr(bookingErrorText(er.code));
      if (er.code === 'not_booked' && onChanged) onChanged();
    }
    setBusy(false);
  };

  const row = (b, isPast) => (
    <li key={b.id} data-testid="my-session" style={{ paddingTop: 12, paddingBottom: 12, borderTop: `1px solid ${V.divider}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: isPast ? V.text2 : V.text }}>{b.title}</div>
        {(isPast || b.status !== 'booked') && <span style={{ fontSize: 11, fontWeight: 700, color: b.status === 'cancelled' || b.status === 'no_show' ? V.danger : V.text3 }}>{statusLabel(b.status)}</span>}
      </div>
      <div style={{ fontSize: 12, color: V.text2, marginTop: 2 }}>{whenLabel(b, tz)}</div>
      <div style={{ fontSize: 12, color: V.text3, marginTop: 2 }}>{kindLabel(b.kind)}{b.location ? ` · ${b.location}` : ''}{b.coach ? ` · ${personName(b.coach)}` : ''}</div>
      {b.note && <div style={{ fontSize: 12, color: V.text3, marginTop: 4, fontStyle: 'italic' }}>“{b.note}”</div>}
      {b.cancel_reason && <div style={{ fontSize: 12, color: V.text3, marginTop: 4 }}>Reason: {b.cancel_reason}</div>}
      {!isPast && (cancelling === b.id ? (
        <div style={{ marginTop: 10 }}>
          <label htmlFor={`cancel-${b.id}`} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: V.text, marginBottom: 6 }}>Reason <span style={{ fontWeight: 400, color: V.text3 }}>(optional, shared with your coach)</span></label>
          <textarea id={`cancel-${b.id}`} value={reason} maxLength={CANCEL_REASON_MAX} rows={2} onChange={e => setReason(e.target.value)} style={inputStyle()} />
          {err && <div role="alert" style={{ fontSize: 12, color: V.danger, fontWeight: 600, marginTop: 6 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Btn v="secondary" onClick={() => { setCancelling(null); setReason(''); setErr(null); }} s={{ flex: 1, minHeight: 40 }}>Keep it</Btn>
            <Btn v="danger" onClick={() => cancel(b)} disabled={busy} s={{ flex: 1, minHeight: 40 }}>{busy ? 'Cancelling…' : 'Cancel session'}</Btn>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Btn v="small" onClick={() => addToCalendar(b)}>{Icons.calendar({ size: 13, color: V.accent })} Add to calendar</Btn>
          {canCancel(b) && <Btn v="ghost" onClick={() => { setCancelling(b.id); setReason(''); setErr(null); }} s={{ minHeight: 36, fontSize: 12, color: V.danger }}>Cancel</Btn>}
        </div>
      ))}
    </li>
  );

  return (
    <FullSheet title="My sessions" ariaLabel="My sessions" onClose={onClose} closeLabel="Close" zIndex={9991}>
      <h2 ref={headRef} tabIndex={-1} style={h2}>My sessions</h2>
      {onBook && <div style={{ marginTop: 12 }}><Btn v="small" onClick={onBook}>{Icons.plus({ size: 13, color: V.accent })} Book a session</Btn></div>}
      <div style={{ fontSize: 11, fontWeight: 700, color: V.text3, letterSpacing: '.08em', textTransform: 'uppercase', marginTop: 18 }}>Upcoming</div>
      {upcoming.length ? <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{upcoming.map(b => row(b, false))}</ul>
        : <div style={{ fontSize: 13, color: V.text3, paddingTop: 8 }}>Nothing booked.</div>}
      {past.length > 0 && <>
        <div style={{ fontSize: 11, fontWeight: 700, color: V.text3, letterSpacing: '.08em', textTransform: 'uppercase', marginTop: 22 }}>Past and cancelled</div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{past.slice(0, 10).map(b => row(b, true))}</ul>
      </>}
    </FullSheet>
  );
}
